import { EditorNode } from '../core/EditorNode';
import { getToken } from '@/lib/token';
import { getApiBaseUrl as getApiBaseUrlFromEnv, resolveEnvironment } from '@seede/api-client';

const getApiBaseUrl = () => {
  return getApiBaseUrlFromEnv(resolveEnvironment());
};

const FileIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-black/60"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><line x1="10" y1="9" x2="8" y2="9"></line></svg>`;

const SpinnerIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-black/60 animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg>`;

const CloseIcon = `<svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" /></svg>`;

const truncateFilename = (filename, maxLength = 20) => {
  if (!filename) return 'Document';
  if (filename.length <= maxLength) return filename;

  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return filename.slice(0, maxLength - 3) + '...';
  }

  const name = filename.slice(0, lastDotIndex);
  const ext = filename.slice(lastDotIndex);
  const availableLength = maxLength - ext.length - 3;

  if (availableLength > 0) {
    return name.slice(0, availableLength) + '...' + ext;
  }

  return filename.slice(0, maxLength - 3) + '...';
};

export class DocumentNode extends EditorNode {
  constructor() {
    super('document');
  }

  render(data, context) {
    const { makeDraggable, onClick, onRemove } = context;
    const asset = data;
    const filename = asset.filename || asset.name || 'Document';
    let isParsed = !!(asset.parsed || asset.parsed_key);
    const isLoading = !!asset.loading;
    const { id } = asset;

    const tagEl = document.createElement('span');
    tagEl.contentEditable = 'false';
    tagEl.setAttribute('data-type', 'document');
    tagEl.setAttribute('data-asset-id', asset.id);
    tagEl.setAttribute('data-filename', filename);
    tagEl.setAttribute('data-parsed', isParsed ? 'true' : 'false');
    tagEl.setAttribute('data-loading', isLoading ? 'true' : 'false');
    tagEl.setAttribute('title', filename);

    tagEl.className =
      'document-tag group relative inline-flex h-7 items-center gap-1.5 pl-1 pr-2.5 mx-0.5 rounded-full bg-black/5 border border-black/5 hover:border-black/10 transition-all duration-300 select-none align-middle cursor-grab draggable-item';

    if (makeDraggable) {
      makeDraggable(tagEl);
    }

    const iconContainer = document.createElement('div');
    iconContainer.className =
      'document-tag-icon w-5 h-5 flex items-center justify-center bg-white rounded-full shadow-sm ring-1 ring-black/5 flex-shrink-0';

    const showSpinner = isLoading || !isParsed;
    iconContainer.innerHTML = showSpinner ? SpinnerIcon : FileIcon;

    const labelEl = document.createElement('span');
    labelEl.className =
      'text-black/80 text-xs font-medium tracking-tight leading-none flex items-center gap-1 pointer-events-none';

    const nameSpan = document.createElement('span');
    nameSpan.className = 'max-w-[120px] truncate';
    nameSpan.textContent = truncateFilename(filename);
    labelEl.appendChild(nameSpan);

    const statusSpan = document.createElement('span');
    statusSpan.className =
      'document-tag-status text-black/40 text-[10px] font-normal whitespace-nowrap';
    if (isLoading) {
      statusSpan.textContent = 'Uploading...';
    } else if (!isParsed) {
      statusSpan.textContent = 'Parsing...';
    }
    labelEl.appendChild(statusSpan);

    tagEl.appendChild(iconContainer);
    tagEl.appendChild(labelEl);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className =
      'absolute -top-1 -right-1 w-4 h-4 bg-black text-white/90 rounded-full flex items-center justify-center shadow-md transition-all duration-200 opacity-0 group-hover:opacity-100 hover:scale-110';
    closeBtn.title = 'Remove';
    closeBtn.innerHTML = CloseIcon;

    closeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (onRemove) {
        onRemove(asset, tagEl);
      } else {
        tagEl.remove();
      }
    });
    tagEl.appendChild(closeBtn);

    if (onClick) {
      tagEl.addEventListener('click', (e) => {
        if (tagEl.classList.contains('dragging')) return;
        e.preventDefault();
        e.stopPropagation();
        onClick(asset, tagEl, e);
      });
    }

    if (!isParsed && !isLoading && id) {
      this.triggerParse(id, tagEl, iconContainer, statusSpan);
    }

    return tagEl;
  }

  async triggerParse(id, tagEl, iconContainer, statusSpan) {
    try {
      const token = getToken();
      if (!token) return;

      const apiBase = getApiBaseUrl();
      const response = await fetch(`${apiBase}/asset/${id}/parse`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`parse failed: ${response.status}`);
      }
      const parsedAsset = await response.json();
      if (!parsedAsset?.parsed_key) {
        throw new Error('parse failed: missing parsed_key');
      }

      tagEl.setAttribute('data-parsed', 'true');
      tagEl.removeAttribute('data-parse-failed');
      iconContainer.innerHTML = FileIcon;
      statusSpan.textContent = '';
    } catch (error) {
      console.error('Error triggering document parse:', error);
      tagEl.setAttribute('data-parsed', 'false');
      tagEl.setAttribute('data-parse-failed', 'true');
      iconContainer.innerHTML = FileIcon;
      statusSpan.textContent = 'Parse failed';
    }
  }

  update(element, data, _context) {
    // Support partial updates
    const currentId = element.getAttribute('data-asset-id');
    const asset = {
      id: data.id || currentId,
      filename: data.filename || data.name || element.getAttribute('data-filename'),
      parsed:
        data.parsed !== undefined ? data.parsed : element.getAttribute('data-parsed') === 'true',
      loading: data.loading,
    };

    // Logic from updateDocumentTagAfterUpload and updateDocumentTagParsedStatus
    const filename = asset.filename || 'Document';
    const isParsed = !!asset.parsed;
    const isLoading = !!asset.loading;
    const parseFailed = element.getAttribute('data-parse-failed') === 'true';

    element.setAttribute('data-asset-id', asset.id);
    element.setAttribute('data-filename', filename);
    element.setAttribute('data-parsed', isParsed ? 'true' : 'false');
    element.setAttribute('data-loading', isLoading ? 'true' : 'false');
    element.setAttribute('title', filename);
    if (isParsed) {
      element.removeAttribute('data-parse-failed');
    }

    const iconContainer = element.querySelector('.document-tag-icon');
    if (iconContainer) {
      iconContainer.innerHTML = isParsed && !isLoading ? FileIcon : SpinnerIcon;
      if (parseFailed && !isParsed) {
        iconContainer.innerHTML = FileIcon;
      }
    }

    const nameSpan = element.querySelector('.max-w-\\[120px\\]');
    if (nameSpan) {
      nameSpan.textContent = truncateFilename(filename);
    }

    const statusSpan = element.querySelector('.document-tag-status');
    if (statusSpan) {
      if (isLoading) {
        statusSpan.textContent = 'Uploading...';
      } else if (parseFailed) {
        statusSpan.textContent = 'Parse failed';
      } else if (!isParsed) {
        statusSpan.textContent = 'Parsing...';
      } else {
        statusSpan.textContent = '';
      }
    }

    if (!isParsed && !isLoading && asset.id && !parseFailed) {
      this.triggerParse(asset.id, element, iconContainer, statusSpan);
    }
  }

  serialize(element) {
    const filename = element.getAttribute('data-filename') || '';
    const id = element.getAttribute('data-asset-id') || '';

    return `
  @VeesoDocument(${JSON.stringify({
    filename: filename,
    id: id,
  })})`;
  }
}
